<script setup lang="ts">
import type {
	OrdenationDropdownOption,
	OrdenationDropdownProps,
	OrdenationSelectWrapperVariant
} from '.';
import type { ButtonVariants } from '~/components/ui/button';
import type Select from '~/components/ui/select/Select.vue';
import type { ClassValue } from 'clsx';

import { useBreakpoints } from '@vueuse/core';
import { defaultOrdenationOptions } from '~/constants';
import { cn } from '~/utils';
import { screenSizesInPixels } from '~~/utils';
import {
	DialogContent,
	DialogDescription,
	DialogPortal,
	DialogTitle
} from 'reka-ui';

import { Dialog } from '~/components/ui/dialog';

import IconMaterialSymbolsLightArrowBack from '~icons/material-symbols-light/arrow-back';
import IconMaterialSymbolsLightCheck from '~icons/material-symbols-light/check';

import {
	ordenationSelectLabelVariant,
	ordenationSelectWrapperVariants
} from '.';

interface Props extends OrdenationDropdownProps {
	isTransitioning?: boolean;
	/** `aboveSheet` lifts the control over the sheet handle's touch area below lg. */
	layer?: OrdenationSelectWrapperVariant['layer'];
	options?: OrdenationDropdownOption[];
	defaultSortBy?: OrdenationDropdownOption['sortBy'];
	defaultOrderBy?: OrdenationDropdownOption['orderBy'];
	buttonVariant?: ButtonVariants['variant'];
	buttonSize?: ButtonVariants['size'];
	buttonClasses?: ClassValue;
}

const props = withDefaults(defineProps<Props>(), {
	labelVariant: 'default',
	isTransitioning: false,
	layer: 'aboveSheet',
	defaultSortBy: undefined,
	defaultOrderBy: 'asc',
	options: () => defaultOrdenationOptions,
	buttonVariant: 'secondary',
	buttonSize: 'sm'
});

const emit = defineEmits<{
	(
		e: 'updateSelection',
		payload: {
			sortBy: OrdenationDropdownOption['sortBy'];
			orderBy: OrdenationDropdownOption['orderBy'];
		}
	): void;
	(e: 'reorder-clicked', value: OrdenationDropdownOption['value']): void;
}>();

const sortBy = defineModel<OrdenationDropdownOption['sortBy']>('sortBy');
const orderBy = defineModel<OrdenationDropdownOption['orderBy']>('orderBy');
const modelValue = ref<string | undefined>(props.defaultSortBy);
const label = ref<string | undefined>();

const hasUserInteracted = ref(false);

const breakpoints = useBreakpoints(screenSizesInPixels);
const isDesktop = breakpoints.greaterOrEqual('lg');

// Mobile modal state
const isMobileModalOpen = ref(false);

// Track select state (desktop only)
const open = ref(false);
const pendingOpen = ref(false);
let transitionEndTimestamp = 0;
let transitionProbeTimeout: ReturnType<typeof setTimeout> | null = null;
const gracePeriodMs = 3000;
let openProbeTimeout: ReturnType<typeof setTimeout> | null = null;
let graceDelayTimeout: ReturnType<typeof setTimeout> | null = null;

const debugSafari = (_message: string, _data?: any) => {};

function isInGracePeriodNow(): boolean {
	const timeSinceTransitionEnd = Date.now() - transitionEndTimestamp;
	return transitionEndTimestamp > 0 && timeSinceTransitionEnd < gracePeriodMs;
}

function handleTransitionEnd() {
	if (pendingOpen.value) {
		debugSafari('handleTransitionEnd called - OPENING SELECT');
		pendingOpen.value = false;
		open.value = true;
	}
}

watch(
	() => props.isTransitioning,
	(isTransitioning, wasTransitioning) => {
		if (wasTransitioning && !isTransitioning) {
			transitionEndTimestamp = Date.now();

			if (pendingOpen.value) {
				handleTransitionEnd();
			}
		}
	}
);

function handleOpenChange(isOpen: boolean) {
	if (!isOpen) {
		open.value = false;
		pendingOpen.value = false;
		if (openProbeTimeout) {
			clearTimeout(openProbeTimeout);
			openProbeTimeout = null;
		}
		if (graceDelayTimeout) {
			clearTimeout(graceDelayTimeout);
			graceDelayTimeout = null;
		}
		return;
	}

	if (!isDesktop.value) {
		isMobileModalOpen.value = true;
		return;
	}

	if (props.isTransitioning || isInGracePeriodNow()) {
		pendingOpen.value = true;
		open.value = false;

		if (!props.isTransitioning) {
			if (graceDelayTimeout) {
				clearTimeout(graceDelayTimeout);
			}
			graceDelayTimeout = setTimeout(() => {
				if (pendingOpen.value) handleTransitionEnd();
				graceDelayTimeout = null;
			}, 500);
		}
		return;
	}

	pendingOpen.value = true;
	open.value = false;
	if (openProbeTimeout) clearTimeout(openProbeTimeout);
	openProbeTimeout = setTimeout(() => {
		if (!props.isTransitioning && pendingOpen.value) {
			handleTransitionEnd();
		}
		openProbeTimeout = null;
	}, 150);
}

function clearOrdenationSelect() {
	modelValue.value = undefined;
	label.value = undefined;
	sortBy.value = undefined;
	orderBy.value = undefined;
	emit('updateSelection', {
		sortBy: undefined,
		orderBy: undefined
	});

	emit('reorder-clicked', props.options[0]?.value);
}

function updateModelValue(value: string) {
	hasUserInteracted.value = true;

	if (
		value === 'default_ordenation' &&
		modelValue.value === 'default_ordenation'
	) {
		clearOrdenationSelect();
		return;
	}

	modelValue.value = value;
	const option = props.options.find(option => option.value === value);

	if (!option) return;
	modelValue.value = option.value;
	label.value = option.label;
	sortBy.value = option.sortBy;
	orderBy.value = option.orderBy;

	emit('updateSelection', {
		sortBy: option.sortBy,
		orderBy: option.orderBy
	});

	emit('reorder-clicked', option.value);
}

function selectMobileOption(value: string) {
	updateModelValue(value);
	isMobileModalOpen.value = false;
}

watch(
	[sortBy, orderBy],
	([newSortBy, newOrderBy]) => {
		if (
			!newSortBy &&
			!newOrderBy &&
			!label.value &&
			!hasUserInteracted.value &&
			props.defaultSortBy &&
			props.defaultOrderBy
		) {
			const defaultOption = props.options.find(
				option =>
					option.sortBy === props.defaultSortBy &&
					option.orderBy === props.defaultOrderBy
			);
			if (defaultOption) {
				label.value = defaultOption.label;
				modelValue.value = defaultOption.value;
				return;
			}
		}

		const currentOption = props.options.find(
			option => option.sortBy === newSortBy && option.orderBy === newOrderBy
		);

		if (currentOption) {
			if (!label.value || modelValue.value !== currentOption.value) {
				label.value = currentOption.label;
				modelValue.value = currentOption.value;
			}
		}
	},
	{ immediate: true }
);

onUnmounted(() => {
	if (openProbeTimeout) {
		clearTimeout(openProbeTimeout);
		openProbeTimeout = null;
	}
	if (graceDelayTimeout) {
		clearTimeout(graceDelayTimeout);
		graceDelayTimeout = null;
	}
});
</script>

<template>
	<div
		:class="
			cn(ordenationSelectWrapperVariants({ layer }), {
				'cursor-pointer': isTransitioning
			})
		"
	>
		<Select
			:model-value="modelValue"
			data-testid="select-ordenation"
			class="h-[50px] border border-white-dark-4 p-0!"
			:open="open"
			@update:model-value="updateModelValue"
			@update:open="handleOpenChange"
		>
			<SelectTrigger
				class="h-10 w-min border-none p-0 shadow-none focus:border-none focus:ring-0"
				arrow-class="hidden"
			>
				<Button
					:variant="props.buttonVariant"
					:size="props.buttonSize"
					:is-skeleton-loading="false"
					class="h-10 px-3 focus:border-none md:px-4"
					:class="
						cn(
							{
								'w-auto sm:w-[170px] lg:w-auto xl:w-[170px]':
									labelVariant === 'default'
							},
							buttonClasses
						)
					"
				>
					<IconMaterialSymbolsLightSwapVert class="size-5 text-primary" />
					<p :class="ordenationSelectLabelVariant({ variant: labelVariant })">
						{{ label }}
					</p>
				</Button>
			</SelectTrigger>

			<SelectContent
				class="absolute top-full mt-2 min-w-[200px] border border-white-dark-3 bg-white px-0 py-2 shadow-[0_1px_3px_1px_#00000026] md:min-w-[250px] max-h-[calc(var(--reka-select-content-available-height,100dvh)-8px)]! overflow-y-auto!"
				:class="{
					'translate-x-[-155px] md:-translate-x-20 lg:translate-x-[-196px] xl:-translate-x-20':
						labelVariant === 'default',
					'translate-x-[-195px] lg:-translate-x-40': labelVariant !== 'default'
				}"
			>
				<SelectGroup class="p-0!">
					<SelectItem
						v-for="option in options"
						:key="`ordenation-select-option-${option.value}`"
						:value="option.value!"
						class="cursor-pointer whitespace-nowrap px-4 py-3 text-primary focus:bg-white-dark-1 focus:text-primary"
					>
						{{ option.label }}
					</SelectItem>
				</SelectGroup>
			</SelectContent>
		</Select>

		<!-- Mobile/Tablet modal (below lg) -->
		<ClientOnly>
			<Dialog
				:open="isMobileModalOpen && !isDesktop"
				@update:open="isMobileModalOpen = $event"
			>
				<DialogPortal>
					<Transition
						enter-active-class="transition-transform duration-300 ease-out"
						enter-from-class="translate-x-full"
						enter-to-class="translate-x-0"
						leave-active-class="transition-transform duration-300 ease-in"
						leave-from-class="translate-x-0"
						leave-to-class="translate-x-full"
					>
						<DialogContent
							class="fixed inset-0 z-200 flex flex-col bg-white lg:hidden focus-visible:outline-none"
							@close-auto-focus.prevent
						>
							<DialogTitle class="sr-only">Ordenar por</DialogTitle>
							<DialogDescription class="sr-only">
								Selecione uma opção de ordenação
							</DialogDescription>
							<header
								class="relative flex items-center border-b border-white-dark-3 px-4 py-4"
							>
								<button
									class="flex items-center gap-2 text-primary"
									aria-label="Voltar"
									@click="isMobileModalOpen = false"
								>
									<IconMaterialSymbolsLightArrowBack class="size-6" />
									<span class="text-base font-normal">Voltar</span>
								</button>
								<p
									class="absolute left-1/2 -translate-x-1/2 text-base font-normal text-primary"
								>
									Ordenar por
								</p>
							</header>

							<ul class="flex flex-col overflow-y-auto">
								<li
									v-for="option in options"
									:key="`mobile-sort-option-${option.value}`"
								>
									<button
										class="flex w-full items-center justify-between px-4 py-4 text-left text-base font-normal text-primary"
										@click="selectMobileOption(option.value!)"
									>
										{{ option.label }}
										<IconMaterialSymbolsLightCheck
											v-if="modelValue === option.value"
											class="size-5 text-primary"
										/>
									</button>
								</li>
							</ul>
						</DialogContent>
					</Transition>
				</DialogPortal>
			</Dialog>
		</ClientOnly>
	</div>
</template>
